import React, { useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import GlassCombobox from "./GlassCombobox";
import RetailerLogo from "./RetailerLogo";
import "./AutoSourcing.css";

// Customer-facing status. Avoid engineering language (adapter / validation /
// provisional / registry / operational). Runnable → "Ready" (a selected row
// shows a check), otherwise "Coming soon". Never expose "Available".
export function sourceStatus(source) {
  if (source.enabled && source.operational) return "Ready";
  return "Coming soon";
}

export function canSelectSource(source) {
  return Boolean(source.enabled && source.operational);
}

// Top Sources come from the backend flag only — never re-derived here.
export function isTopSource(source) {
  return Boolean(canSelectSource(source) && source.top_source);
}

export const RETAILER_LIST_MAX_HEIGHT = 460;
const SKELETON_ROWS = 6;

const TABS = [
  ["all", "All"],
  ["ready", "Ready"],
  ["top", "Top Sources"],
];

const AVAILABILITY = [
  ["all", "All"],
  ["ready", "Ready"],
  ["soon", "Coming soon"],
];

export const VERTICAL_LABELS = {
  home_improvement: "Home Improvement",
  beauty: "Beauty",
  fashion: "Fashion",
  electronics: "Electronics",
  automotive: "Automotive",
  home: "Home",
  sports: "Sports",
  pet: "Pet",
  office: "Office",
  grocery: "Grocery",
  baby: "Baby",
  toys: "Toys",
  other: "Other",
};
const VERTICAL_ORDER = Object.keys(VERTICAL_LABELS);

export const TOP_SOURCES_INTRO =
  "Retailers optimized for ongoing product discovery based on source reliability, product coverage, and Ezisell sourcing capabilities.";
export const TOP_SOURCES_EMPTY =
  "No Top Sources available yet. We\u2019re validating retailers for continuous sourcing.";

const primaryVertical = (source) => {
  const first = Array.isArray(source.source_verticals) ? source.source_verticals[0] : null;
  return VERTICAL_LABELS[first] ? first : "other";
};
const verticalsOf = (source) => {
  const list = (source.source_verticals || []).filter((v) => VERTICAL_LABELS[v]);
  return list.length ? list : ["other"];
};

const byName = (a, b) => (a.name || a.domain || "").localeCompare(b.name || b.domain || "");
const byRankThenName = (a, b) => {
  const ra = Number.isFinite(a.top_source_rank) ? a.top_source_rank : Infinity;
  const rb = Number.isFinite(b.top_source_rank) ? b.top_source_rank : Infinity;
  return ra !== rb ? ra - rb : byName(a, b);
};

function capabilityText(source) {
  const caps = source.source_capabilities || [];
  const parts = [];
  if (caps.includes("categories")) {
    const n = source.category_support?.category_count;
    parts.push(n ? `${n.toLocaleString()} categories` : "Categories");
  }
  if (caps.includes("search")) parts.push("Custom Search");
  if (caps.includes("identifier")) parts.push("Identifier");
  if (caps.includes("title_attributes")) parts.push("Title + Attributes");
  return parts.join(" \u00b7 ");
}

const isSelected = (value, id) => (Array.isArray(value) ? value.includes(id) : value === id);

function selectedIds(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
}

export default function RetailerPicker({
  sources = [],
  value,
  onChange,
  multiple = false,
  tab: controlledTab,
  onTabChange,
  loading = false,
  error = null,
  refreshFailed = false,
  notice = null,
  onRetry,
  "aria-label": ariaLabel,
}) {
  const resolvedAriaLabel = ariaLabel || (multiple ? "Select retailers" : "Select a retailer");
  const [query, setQuery] = useState("");
  const [localTab, setLocalTab] = useState("all");
  const [availability, setAvailability] = useState("all");
  const [vertical, setVertical] = useState("all");
  const tabRefs = useRef({});
  const tab = controlledTab ?? localTab;
  const changeTab = (id) => {
    if (onTabChange) onTabChange(id);
    else setLocalTab(id);
  };

  // Ready first, then Coming soon, alphabetical within each; search text is
  // built once per directory, not per keystroke.
  const indexed = useMemo(
    () =>
      sources
        .map((source) => ({
          source,
          ready: canSelectSource(source),
          top: isTopSource(source),
          haystack: `${source.name || ""} ${source.domain || ""} ${
            Array.isArray(source.aliases) ? source.aliases.join(" ") : ""
          }`.toLowerCase(),
        }))
        .sort((a, b) => {
          if (a.ready !== b.ready) return a.ready ? -1 : 1;
          return byName(a.source, b.source);
        }),
    [sources],
  );

  const topVerticals = useMemo(() => {
    const present = new Set();
    indexed.forEach(({ source, top }) => {
      if (top) verticalsOf(source).forEach((v) => present.add(v));
    });
    return VERTICAL_ORDER.filter((v) => present.has(v));
  }, [indexed]);
  const activeVertical = topVerticals.includes(vertical) ? vertical : "all";
  const topCount = useMemo(() => indexed.filter((i) => i.top).length, [indexed]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = indexed
      .filter(({ source, ready, top, haystack }) => {
        if (q && !haystack.includes(q)) return false;
        if (tab === "ready" && !ready) return false;
        if (tab === "top") {
          if (!top) return false;
          if (activeVertical !== "all" && !verticalsOf(source).includes(activeVertical)) return false;
        }
        if (tab === "all" && availability === "ready" && !ready) return false;
        if (tab === "all" && availability === "soon" && ready) return false;
        return true;
      })
      .map(({ source }) => source);
    if (tab !== "top") return rows;
    if (activeVertical !== "all") return rows.sort(byRankThenName);
    return rows.sort((a, b) => {
      const va = VERTICAL_ORDER.indexOf(primaryVertical(a));
      const vb = VERTICAL_ORDER.indexOf(primaryVertical(b));
      return va !== vb ? va - vb : byRankThenName(a, b);
    });
  }, [indexed, query, tab, availability, activeVertical]);

  const ids = selectedIds(value);
  const selectedSources = ids
    .map((id) => sources.find((s) => s.source_id === id))
    .filter(Boolean);
  const primarySelected = selectedSources[0] || null;

  const toggleRetailer = (source) => {
    if (!canSelectSource(source)) return;
    const sid = source.source_id;
    if (!multiple) {
      onChange(sid);
      return;
    }
    const next = ids.includes(sid) ? ids.filter((id) => id !== sid) : [...ids, sid];
    onChange(next);
  };

  const clearAll = () => onChange(multiple ? [] : undefined);

  const removeOne = (sid) => {
    if (!multiple) {
      onChange(undefined);
      return;
    }
    onChange(ids.filter((id) => id !== sid));
  };

  const grouped = tab === "top" && activeVertical === "all";
  const options = filtered.map((source, index) => {
    const selectable = canSelectSource(source);
    const top = isTopSource(source);
    const group = grouped ? primaryVertical(source) : null;
    const newGroup = grouped && (index === 0 || primaryVertical(filtered[index - 1]) !== group);
    const caps = tab === "top" ? capabilityText(source) : "";
    const selected = isSelected(value, source.source_id);
    return {
      key: source.source_id,
      label: source.name || source.domain,
      description: caps ? `${source.domain} \u00b7 ${caps}` : source.domain,
      leading: (
        <span className="as-retailer-option-leading">
          {multiple && selectable ? (
            <span
              className={`as-retailer-check${selected ? " is-on" : ""}`}
              aria-hidden="true"
              data-testid={`retailer-check-${source.source_id}`}
            />
          ) : null}
          <RetailerLogo source={source} />
        </span>
      ),
      trailing: !selectable ? (
        <span className="as-coming-soon">Coming soon</span>
      ) : top ? (
        <span className="as-continuous-badge" data-testid="retailer-continuous-badge">Continuous</span>
      ) : null,
      before: newGroup ? (
        <div className="as-retailer-group" role="presentation" data-testid={`retailer-group-${group}`}>
          {VERTICAL_LABELS[group]}
        </div>
      ) : null,
      disabled: !selectable,
      selected,
      keepOpen: multiple && selectable,
      title: selectable ? undefined : "This retailer is being prepared for sourcing.",
      onSelect: () => toggleRetailer(source),
    };
  });

  const cold = sources.length === 0;
  const soonCount = useMemo(() => indexed.filter((i) => !i.ready).length, [indexed]);
  const availabilityOptions = useMemo(
    () => (soonCount > 0 ? AVAILABILITY : AVAILABILITY.filter(([id]) => id !== "soon")),
    [soonCount],
  );
  const status = error && cold ? (
    <div className="as-glass-empty as-retailer-error" data-testid="retailer-picker-error">
      <span>Couldn&apos;t load retailers.</span>
      {onRetry ? (
        <button type="button" className="as-retry" onClick={onRetry} data-testid="retailer-picker-retry">
          Retry
        </button>
      ) : null}
    </div>
  ) : loading && cold ? (
    <div aria-hidden="true" data-testid="retailer-picker-skeleton">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <div key={i} className="as-retailer-skeleton">
          <span className="as-retailer-skeleton-logo" />
          <span className="as-retailer-skeleton-copy">
            <span />
            <span />
          </span>
        </div>
      ))}
    </div>
  ) : tab === "top" && topCount === 0 && !cold ? (
    <div className="as-glass-empty" data-testid="retailer-top-empty">{TOP_SOURCES_EMPTY}</div>
  ) : filtered.length === 0 && !cold ? (
    <div className="as-glass-empty">No retailers match your search.</div>
  ) : null;

  const footer = notice ? (
    <span className="as-retailer-notice" data-testid="retailer-picker-notice">{notice}</span>
  ) : refreshFailed ? (
    <span data-testid="retailer-picker-refresh-failed">Couldn&apos;t refresh retailer list.</span>
  ) : soonCount > 0 ? (
    "More retailers are being added."
  ) : (
    `${indexed.filter((i) => i.ready).length} ready retailers`
  );

  const chipGroup = (items, current, onPick, label, testPrefix) => (
    <div className="as-retailer-availability" role="radiogroup" aria-label={label}>
      {items.map(([id, text]) => (
        <button
          key={id}
          type="button"
          role="radio"
          aria-checked={current === id}
          className={current === id ? "active" : ""}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPick(id)}
          data-testid={`${testPrefix}-${id}`}
        >
          {text}
        </button>
      ))}
    </div>
  );

  let header = null;
  if (tab === "all" && availabilityOptions.length > 1) {
    header = chipGroup(
      availabilityOptions,
      availabilityOptions.some(([id]) => id === availability) ? availability : "all",
      setAvailability,
      "Availability",
      "retailer-availability",
    );
  } else if (tab === "top" && topCount > 0) {
    header = (
      <div className="as-top-sources-head" data-testid="retailer-top-intro">
        <p>{TOP_SOURCES_INTRO}</p>
        {topVerticals.length > 1
          ? chipGroup(
              [["all", "All"], ...topVerticals.map((v) => [v, VERTICAL_LABELS[v]])],
              activeVertical,
              setVertical,
              "Category",
              "retailer-vertical",
            )
          : null}
      </div>
    );
  }

  const onTabKeyDown = (e) => {
    const ids = TABS.map(([id]) => id);
    const at = ids.indexOf(tab);
    let next = null;
    if (e.key === "ArrowRight") next = ids[(at + 1) % ids.length];
    else if (e.key === "ArrowLeft") next = ids[(at - 1 + ids.length) % ids.length];
    else if (e.key === "Home") next = ids[0];
    else if (e.key === "End") next = ids[ids.length - 1];
    if (!next) return;
    e.preventDefault();
    changeTab(next);
    tabRefs.current[next]?.focus();
  };

  const triggerLabel = (() => {
    if (multiple && selectedSources.length > 1) {
      return (
        <span className="as-retailer-trigger-copy">
          <strong>{selectedSources.length} retailers selected</strong>
          <small>{selectedSources.map((s) => s.name || s.domain).slice(0, 3).join(", ")}
            {selectedSources.length > 3 ? ` +${selectedSources.length - 3}` : ""}
          </small>
        </span>
      );
    }
    if (primarySelected) {
      return (
        <span className="as-retailer-trigger-copy">
          <strong>{primarySelected.name || primarySelected.domain}</strong>
          <small>{primarySelected.domain}</small>
        </span>
      );
    }
    return null;
  })();

  return (
    <div className={`as-retailer${multiple ? " as-retailer-multi" : ""}`} data-testid="retailer-picker">
      <div className="as-retailer-tabs" role="tablist" aria-label="Retailers" onKeyDown={onTabKeyDown}>
        {TABS.map(([id, label]) => (
          <button
            key={id}
            ref={(el) => {
              tabRefs.current[id] = el;
            }}
            type="button"
            role="tab"
            aria-selected={tab === id}
            tabIndex={tab === id ? 0 : -1}
            className={`${tab === id ? "active" : ""}${id === "top" ? " as-retailer-tab-top" : ""}`}
            onClick={() => changeTab(id)}
            data-testid={`retailer-tab-${id}`}
          >
            {id === "top" ? <span aria-hidden="true" className="as-retailer-tab-spark">{"\u2726"}</span> : null}
            {label}
          </button>
        ))}
      </div>

      <GlassCombobox
        testId="retailer-combobox"
        triggerClassName="as-retailer-trigger"
        ariaLabel={resolvedAriaLabel}
        leading={
          primarySelected ? (
            <RetailerLogo source={primarySelected} />
          ) : (
            <span className="as-retailer-logo" aria-hidden="true">
              <Search className="as-retailer-logo-search" />
            </span>
          )
        }
        label={triggerLabel}
        placeholder={
          <span className="as-retailer-trigger-copy">
            <strong>
              {tab === "top"
                ? "Select Top Sources"
                : multiple
                  ? "Select retailers"
                  : "Select a retailer"}
            </strong>
            <small>
              {tab === "top"
                ? "Retailers ready for continuous sourcing"
                : multiple
                  ? "Choose one or more Ready retailers"
                  : "Search the source catalog"}
            </small>
          </span>
        }
        options={options}
        query={query}
        onQueryChange={setQuery}
        searchPlaceholder="Search retailer or domain..."
        searchLabel="Search retailer or domain"
        listLabel="Retailers"
        status={status}
        header={header}
        maxHeight={RETAILER_LIST_MAX_HEIGHT}
        footer={footer}
        resetKey={`${tab}:${availability}:${activeVertical}`}
      />

      {multiple && selectedSources.length > 0 ? (
        <div className="as-retailer-chips" data-testid="retailer-selected-chips">
          <div className="as-retailer-chips-head">
            <span className="as-retailer-chips-title">
              Selected retailers ({selectedSources.length})
            </span>
            <button
              type="button"
              className="as-retailer-chips-clear"
              onClick={clearAll}
              data-testid="retailer-clear-all"
            >
              Clear all
            </button>
          </div>
          <div className="as-retailer-chip-row" role="list">
            {selectedSources.map((source) => (
              <span key={source.source_id} className="as-retailer-chip" role="listitem">
                <RetailerLogo source={source} size="sm" />
                <span className="as-retailer-chip-name">{source.name || source.domain}</span>
                <button
                  type="button"
                  className="as-retailer-chip-remove"
                  aria-label={`Remove ${source.name || source.domain}`}
                  onClick={() => removeOne(source.source_id)}
                  data-testid={`retailer-chip-remove-${source.source_id}`}
                >
                  ×
                </button>
              </span>
            ))}
            <button
              type="button"
              className="as-retailer-chip-add"
              onClick={() => {
                const trigger = document.querySelector('[data-testid="retailer-combobox-trigger"]');
                trigger?.click?.();
              }}
              data-testid="retailer-add-another"
            >
              + Add another retailer
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
